import { useState } from 'react';
import {
  CheckCircle2,
  Cloud,
  Database,
  Fingerprint,
  LockKeyhole,
  Radar,
  ScanSearch,
} from 'lucide-react';

const services = [
  {
    title: 'Security Assessment',
    image: 'https://ik.imagekit.io/7wqj9br0b/PTKCS/Services/cyber%20security/security_assessment.png',
    imageAlt: 'Security assessment and vulnerability review',
    icon: ScanSearch,
    description:
      'Know where your exposure sits before deciding what to fix. We review applications, infrastructure, configurations and current controls, then connect technical findings to the business processes they could affect.',
    capabilities: [
      'Vulnerability assessment',
      'Configuration and control reviews',
      'Security gap analysis',
      'Risk-based remediation roadmap',
    ],
  },
  {
    title: 'Managed Security Services',
    image: 'https://ik.imagekit.io/7wqj9br0b/PTKCS/Services/cyber%20security/managed_servuces_2.jpg',
    imageAlt: 'Managed security operations and threat monitoring',
    icon: Radar,
    description:
      'Turn security signals into timely decisions. We help bring monitoring, investigation and response into a defined operating rhythm across endpoints, identities, applications and cloud platforms.',
    capabilities: [
      'Security monitoring',
      'Threat detection and triage',
      'Incident response coordination',
      'Security operations reporting',
    ],
  },
  {
    title: 'Identity & Access Management',
    image: 'https://ik.imagekit.io/7wqj9br0b/PTKCS/Services/cyber%20security/iam.png',
    imageAlt: 'Identity and access management controls',
    icon: Fingerprint,
    description:
      'Give the right people the right access at the right time. We map how users and service accounts enter critical systems, reduce unnecessary privilege and make access decisions easier to review.',
    capabilities: [
      'Single sign-on and MFA',
      'Privileged access management',
      'Identity lifecycle workflows',
      'Access review and governance',
    ],
  },
  {
    title: 'Zero Trust Security',
    image: 'https://ik.imagekit.io/7wqj9br0b/PTKCS/Services/cyber%20security/zero_trust_security.png',
    imageAlt: 'Zero trust security architecture',
    icon: LockKeyhole,
    description:
      'Replace broad, assumed access with controls based on identity, device and context. We help define trust boundaries, segment sensitive systems and verify requests continuously.',
    capabilities: [
      'Zero trust architecture',
      'Network segmentation',
      'Conditional access',
      'Endpoint and device controls',
    ],
  },
  {
    title: 'Cloud Security',
    image: 'https://ik.imagekit.io/7wqj9br0b/PTKCS/Services/cyber%20security/cloud_security_2.jpg',
    imageAlt: 'Cloud infrastructure security monitoring',
    icon: Cloud,
    description:
      'Keep cloud environments visible as they change. We review identity, workload and data controls across your cloud estate, address risky configuration drift and connect security checks to deployment workflows.',
    capabilities: [
      'Cloud posture assessment',
      'Workload and container security',
      'Cloud identity and permissions',
      'Logging and configuration controls',
    ],
  },
  {
    title: 'Data Protection & Compliance',
    image: 'https://ik.imagekit.io/7wqj9br0b/PTKCS/Services/cyber%20security/data_compilance.png',
    imageAlt: 'Data protection and compliance safeguards',
    icon: Database,
    description:
      'Protect the information your business depends on. We help identify sensitive data, define who should access it and apply safeguards across storage, movement and retention.',
    capabilities: [
      'Data classification and governance',
      'Encryption and access policies',
      'Privacy and retention controls',
      'Audit evidence and reporting',
    ],
  },
];

export default function CybersecurityServiceTabs() {
  const [activeService, setActiveService] = useState(0);
  const service = services[activeService];

  return (
    <section
      className="bg-[#F7F8FC] px-5 py-16 sm:px-6 lg:px-10 lg:py-12"
      aria-labelledby="cyber-highlights-title"
    >
      <div className="mx-auto max-w-6xl">
        <header className="mx-auto mb-10 max-w-4xl text-center sm:mb-12">
          <h2
            id="cyber-highlights-title"
            className="text-[32px] font-semibold leading-[48px]"
            style={{ color: '#183762' }}
          >
            Our Cybersecurity Services
          </h2>
          <p className="mt-4 text-base font-normal text-center leading-[27px]" style={{ color: '#212121' }}>
            Explore practical security services tailored to your environment, priorities and
            compliance needs.
          </p>
        </header>

        <div className="grid overflow-hidden rounded-lg md:grid-cols-[0.9fr_1.1fr]">
          <div
            className="space-y-2 bg-[#1B1BAA] p-3 sm:p-4"
            role="tablist"
            aria-label="Cybersecurity services"
            aria-orientation="vertical"
          >
            {services.map((item, index) => {
              const Icon = item.icon;
              const isActive = activeService === index;

              return (
                <button
                  key={item.title}
                  id={`cyber-service-tab-${index}`}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls="cyber-service-panel"
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => setActiveService(index)}
                  onKeyDown={(event) => {
                    let nextIndex;
                    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
                      nextIndex = (index + 1) % services.length;
                    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
                      nextIndex = (index - 1 + services.length) % services.length;
                    } else if (event.key === 'Home') {
                      nextIndex = 0;
                    } else if (event.key === 'End') {
                      nextIndex = services.length - 1;
                    } else {
                      return;
                    }

                    event.preventDefault();
                    const tabs = event.currentTarget.parentElement.querySelectorAll('[role="tab"]');
                    tabs[nextIndex].focus();
                    setActiveService(nextIndex);
                  }}
                  className={`flex min-h-[58px] w-full items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm font-semibold transition-colors sm:text-base ${
                    isActive
                      ? 'border-[#00A9E0] bg-[#00A9E0] text-white'
                      : 'border-white/50 bg-transparent text-white hover:bg-white/10'
                  }`}
                >
                  <Icon size={19} strokeWidth={1.8} className="shrink-0" aria-hidden="true" />
                  <span className="flex-1">{item.title}</span>
                  <span aria-hidden="true" className="text-xl leading-none">»</span>
                </button>
              );
            })}
          </div>

          <article
            id="cyber-service-panel"
            role="tabpanel"
            aria-labelledby={`cyber-service-tab-${activeService}`}
            className="overflow-hidden bg-white text-black"
          >
            <img
              src={service.image}
              alt={service.imageAlt}
              className="h-52 w-full object-cover sm:h-60"
              loading="lazy"
              decoding="async"
            />
            <div className="p-5 sm:p-7">
              <h3 className="text-xl text-black font-semibold leading-tight sm:text-2xl">
                {service.title}
              </h3>
              <p className="mt-3 text-black text-base font-normal leading-[27px]">
                {service.description}
              </p>
              <ul className="mt-4 space-y-2">
                {service.capabilities.map((capability) => (
                  <li key={capability} className="flex items-start gap-2 text-sm text-black">
                    <CheckCircle2
                      size={17}
                      className="mt-0.5 shrink-0 text-[#00C3F2]"
                      aria-hidden="true"
                    />
                    <span>{capability}</span>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
